import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, Play, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getProviderIcon } from "~/components/chat/provider-icons";
import { ProviderListSkeleton } from "~/components/loading-skeleton";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { $deleteProviderConnection, $testProviderConnection } from "~/lib/mutations";
import { $getAiProviderConnections } from "~/lib/queries";

export const Route = createFileRoute("/(authenticated)/dashboard/settings/providers/")({
  component: ProvidersSettingsPage,
  pendingComponent: ProviderListSkeleton,
  wrapInSuspense: true,
});

function ProvidersSettingsPage() {
  const queryClient = useQueryClient();

  const { data: providers } = useSuspenseQuery({
    queryKey: ["ai-provider-connections"],
    queryFn: () => $getAiProviderConnections(),
  });

  const getNextResetAt = () => {
    const now = new Date();
    return new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1),
    );
  };

  const handleDelete = async (id: string, displayName: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${displayName}"? This action cannot be undone.`,
      )
    ) {
      try {
        await $deleteProviderConnection({ data: { id } });
        queryClient.invalidateQueries({ queryKey: ["ai-provider-connections"] });
        toast.success("Provider connection deleted");
      } catch {
        toast.error("Failed to delete provider connection");
      }
    }
  };

  const handleTest = async (id: string, displayName: string) => {
    try {
      toast.info(`Testing connection to ${displayName}...`);
      await $testProviderConnection({ data: { connectionId: id } });
      queryClient.invalidateQueries({ queryKey: ["ai-provider-connections"] });
      toast.success(`Connection to ${displayName} successful!`);
    } catch {
      toast.error("Connection test failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">AI Providers</h2>
          <p className="text-muted-foreground text-sm">
            Manage your AI provider connections
          </p>
        </div>
        <Link to="/dashboard/settings/providers/add">
          <Button size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Add Provider
          </Button>
        </Link>
      </div>

      {providers.length === 0 ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No providers configured yet. Add your first provider to start using
            quickprompt.dev.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {providers.map((provider) => {
            const isFreeTier = provider.providerType === "system-free";
            const limit = provider.dailyRequestLimit ?? 0;
            const used = provider.dailyRequestCount ?? 0;
            const resetAt = getNextResetAt();
            const usageText =
              limit > 0
                ? `${Math.min(used, limit)}/${limit} requests used today`
                : "Free tier usage";
            const resetText = `Resets ${formatDistanceToNow(resetAt, {
              addSuffix: true,
            })}`;

            return (
              <Card key={provider.id}>
                <CardContent className="px-3">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="bg-muted text-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-md">
                      {getProviderIcon(provider.provider, "h-5 w-5")}
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {/* Status Dot */}
                        <div
                          className={`h-2 w-2 shrink-0 rounded-full ${
                            provider.isActive ? "bg-green-500" : "bg-red-500"
                          }`}
                        />
                        <h3 className="truncate font-semibold">{provider.displayName}</h3>
                        {provider.isDefault && (
                          <Star className="h-3 w-3 shrink-0 fill-yellow-500 text-yellow-500" />
                        )}
                      </div>
                      <p className="text-muted-foreground truncate text-xs capitalize">
                        {provider.provider.replace("-", " ")}
                      </p>
                      {provider.lastTestSuccess && (
                        <p className="text-muted-foreground mt-0.5 text-xs">
                          Tested{" "}
                          {formatDistanceToNow(new Date(provider.lastTestSuccess), {
                            addSuffix: true,
                          })}
                        </p>
                      )}
                      {isFreeTier && (
                        <p className="text-muted-foreground mt-0.5 text-xs">
                          {usageText}
                        </p>
                      )}
                      {isFreeTier && (
                        <p className="text-muted-foreground mt-0.5 text-xs">
                          {resetText}
                        </p>
                      )}
                      {provider.lastTestError && (
                        <p
                          className="text-destructive mt-0.5 truncate text-xs"
                          title={provider.lastTestError}
                        >
                          {provider.lastTestError}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleTest(provider.id, provider.displayName)}
                        title="Test connection"
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          if (isFreeTier) {
                            toast.error("System provider cannot be removed");
                            return;
                          }
                          handleDelete(provider.id, provider.displayName);
                        }}
                        title={
                          isFreeTier
                            ? "System provider cannot be removed"
                            : "Delete provider"
                        }
                        disabled={isFreeTier}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
